import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CheckboxModule } from 'primeng/checkbox';
import { ShFormControl } from '../form-control/form-control.base';
import { primeSize } from '../models';

/**
 * Checkbox with an optional `label` beside the box (clicking it toggles).
 * The value is a boolean.
 *
 *   <sh-checkbox label="account.login.rememberMe" [(value)]="rememberMe" />
 *
 * Without a `label` (e.g. inside an `sh-field` that has one) the field's
 * label is associated through the shared id.
 */
@Component({
  selector: 'sh-checkbox',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, FormsModule, TranslateModule, CheckboxModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShCheckboxComponent),
      multi: true,
    },
  ],
  host: { class: 'sh-control', '[class.sh-invalid]': 'isInvalid()' },
  template: `
    <p-checkbox
      [binary]="true"
      [ngModel]="!!value()"
      (ngModelChange)="commit($event)"
      [ngModelOptions]="{ standalone: true }"
      [inputId]="controlId()"
      [ariaLabel]="ariaLabel() | translate"
      [readonly]="isReadonly()"
      [disabled]="isDisabled()"
      [size]="pSize()"
      (onBlur)="markTouched()"
    ></p-checkbox>
    <label *ngIf="label()" [attr.for]="controlId()">{{
      label() | translate
    }}</label>
  `,
})
export class ShCheckboxComponent extends ShFormControl<boolean> {
  /** Translation key or plain text, shown beside the box. */
  readonly label = input<string>();

  protected readonly pSize = computed(
    () => primeSize(this.size()) as 'small' | 'large'
  );
}
